import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const billTo = getBillToState(state, state.context.session.billToId).value;
    const shipTo = getShipToState(state, state.context.session.shipToId).value;
    const countries = getCurrentCountries(state);
    const cart = cartState.value;
    const isInValidCountry = countries?.map(
        o => o.abbreviation !== (shipTo?.country?.abbreviation && billTo?.country?.abbreviation),
    )[0];
    return {
        shipTo,
        billTo,
        billToState: getBillToState(state, cart?.billToId),
        billingAddressFormState: state.pages.checkoutShipping.billingAddressFormState,
        shippingAddressFormState: state.pages.checkoutShipping.shippingAddressFormState,
        useBillingAddress: state.pages.checkoutShipping.useBillingAddress,
        useOneTimeShipToAddress: state.pages.checkoutShipping.useOneTimeAddress,
        settings: getSettingsCollection(state),
        cart,
        isInValidCountry,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface CenPoBoxErrorMsgStyles {
    container?: InjectableCss;
    poBoxErrorStyles?: TypographyPresentationProps;
    error?: InjectableCss;
}

export const cenPoBoxErrorMsgStyles: CenPoBoxErrorMsgStyles = {
    container: {},
    poBoxErrorStyles: {
        color: "danger",
        css: css`
            margin-top: 10px;
        `,
    },
    error: {
        css: css`
            padding: 20px;
            padding-left: 0px;
            display: flex;
            justify-content: center;
            color: #e23939;
        `,
    },
};

const styles = cenPoBoxErrorMsgStyles;
const CENPoBoxErrorMessageCheckout: FC<Props> = ({
    shipTo,
    billTo,
    billToState,
    billingAddressFormState,
    shippingAddressFormState,
    useBillingAddress,
    useOneTimeShipToAddress,
    cart,
    settings,
    isInValidCountry,
}) => {
    const poboxRegex = new RegExp((settings as any)?.customSettings?.poBoxValidationRegex);

    const isPoBoxNotValidBillingAddress1 =
        shippingAddressFormState &&
        shippingAddressFormState?.address?.id === cart?.billToId &&
        billingAddressFormState &&
        poboxRegex.test(billingAddressFormState.address.address1);

    const isPoBoxNotValidBillingAddress2 =
        shippingAddressFormState &&
        shippingAddressFormState?.address?.id === cart?.billToId &&
        billingAddressFormState &&
        poboxRegex.test(billingAddressFormState.address.address2);

    const poBoxNotValid =
        (shipTo && !useOneTimeShipToAddress && shipTo.address1 && poboxRegex?.test(shipTo.address1)) ||
        (shipTo && !useOneTimeShipToAddress && shipTo.address2 && poboxRegex?.test(shipTo.address2)) ||
        (shippingAddressFormState?.address?.address1 && poboxRegex?.test(shippingAddressFormState.address.address1)) ||
        (shippingAddressFormState?.address?.address2 && poboxRegex?.test(shippingAddressFormState.address.address2)) ||
        isPoBoxNotValidBillingAddress1 ||
        isPoBoxNotValidBillingAddress2;

    return (
        <>
            {poBoxNotValid && (
                <Typography as="p" {...styles.poBoxErrorStyles}>
                    {siteMessage("Checkout_POBoxErrorMessage")}
                </Typography>
            )}
            {isInValidCountry && shipTo?.country !== null && (
                <StyledWrapper {...styles.error}>{siteMessage("Country_Invalid")}</StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CENPoBoxErrorMessageCheckout),
    definition: {
        group: "Common",
        displayName: "PO Box Error Message",
    },
};
export default widgetModule;
